import {
  DatiSessioneUtente,
  EnumModuloDTONecessitaFirma,
  RichiestaDTO,
  RichiestaModuloDTOPermessiItem,
} from '@framework/api-client';
import { EntityMachine } from '@framework/common-web';
import { SubmissionLogic } from '../../submission/SubmissionLogic';
import { SubmissionModuleLogic } from '../../submission/submission-module/SubmissionModuleLogic';
import { IDelegationData } from '../delegation/DelegationLogic';
import { isManager } from '../delegation/DelegationMachine';
import { IFrontOfficeModule } from '../hooks/useModule';
import { SubmissionModuleState } from './SubmissionModuleState';
import { hasActionPermission } from './hasActionPermission';
import { isModuleWithoutSignAndFullControl } from './isModuleWithoutSignAndFullControled';

export interface IModuleStateMachineRecord {
  delegationData: IDelegationData | null | undefined;
  module: IFrontOfficeModule | null | undefined;
  submission: RichiestaDTO | null | undefined;
}

/**
 * Finite State Machine per la gestione dello stato dei moduli
 * in fase di compilazione richiesta.
 */
export const ModuleStateMachine = EntityMachine.for<
  IModuleStateMachineRecord,
  DatiSessioneUtente
>()
  .withState((record): SubmissionModuleState => {
    if (!record.module) return SubmissionModuleState.None;
    return SubmissionModuleLogic.getState(record.module);
  })
  .withTransition(SubmissionModuleState.Compilation, {
    to: SubmissionModuleState.Sign,
    allowed: (record, user) => {
      if (!record.module) return { allowed: false, message: 'no-module' };
      if (!record.submission) return { allowed: false, message: 'no-submission' }; // prettier-ignore
      const result = SubmissionModuleLogic.needSign(
        record.module,
        record.submission,
        user,
      );
      if (!result) {
        return { allowed: false, message: 'no-sign-needed' };
      }

      if (
        !hasActionPermission(
          record,
          RichiestaModuloDTOPermessiItem.COMPILAZIONE,
        )
      ) {
        return { allowed: false, message: 'no-action-permission' };
      }

      return { allowed: true };
    },
  })
  .withTransition(SubmissionModuleState.Sign, {
    to: SubmissionModuleState.Completed,
    allowed: (record, user) => {
      if (!record.module) return { allowed: false, message: 'no-module' };
      if (!record.submission) return { allowed: false, message: 'no-submission' }; // prettier-ignore
      const result = SubmissionModuleLogic.needSign(
        record.module,
        record.submission,
        user,
      );
      if (!result) {
        return { allowed: false, message: 'no-sign-needed' };
      }

      if (!hasActionPermission(record, RichiestaModuloDTOPermessiItem.FIRMA)) {
        return { allowed: false, message: 'no-action-permission' };
      }

      return { allowed: true };
    },
  })
  .withTransition(SubmissionModuleState.Compilation, {
    to: SubmissionModuleState.Completed,
    allowed: (record, user) => {
      if (!record.module) return { allowed: false, message: 'no-module' };
      if (!record.submission) return { allowed: false, message: 'no-submission' }; // prettier-ignore
      // Possiamo completare direttamente solo se non c'è bisogno di firma
      const result = SubmissionModuleLogic.needSign(
        record.module,
        record.submission,
        user,
      );
      if (result) {
        return { allowed: false, message: 'sign-needed' };
      }

      if (
        !hasActionPermission(
          record,
          RichiestaModuloDTOPermessiItem.COMPILAZIONE,
        )
      ) {
        return { allowed: false, message: 'no-action-permission' };
      }

      return { allowed: true };
    },
  })
  .withTransition(SubmissionModuleState.Sign, {
    to: SubmissionModuleState.Compilation,
    allowed: (record, user) => {
      if (!record.module) return { allowed: false, message: 'no-module' };
      if (!record.submission) return { allowed: false, message: 'no-submission' }; // prettier-ignore
      const result = SubmissionModuleLogic.needSign(
        record.module,
        record.submission,
        user,
      );
      if (!result) {
        return { allowed: false, message: 'no-sign-needed' };
      }

      if (!hasActionPermission(record, RichiestaModuloDTOPermessiItem.FIRMA)) {
        return { allowed: false, message: 'no-action-permission' };
      }

      return { allowed: true };
    },
  })
  .withTransition(SubmissionModuleState.Completed, {
    to: SubmissionModuleState.Compilation,
    allowed: (record, user, state) => {
      if (!isManager(record.delegationData, user)) {
        return { allowed: false, message: 'is-not-manager' };
      }

      return { allowed: true };
    },
  })
  .withAction('.signable', {
    allowed: (record, user, state) => {
      if (!record.module) return { allowed: false };
      return {
        allowed:
          record.module.necessitaFirma !== EnumModuloDTONecessitaFirma.NO,
      };
    },
  })
  .withAction('.editable', {
    allowed: (record, user, state) => {
      if (SubmissionLogic.isSubmissionReadonly(record.submission)) {
        return { allowed: false };
      }

      if (
        !hasActionPermission(
          record,
          RichiestaModuloDTOPermessiItem.COMPILAZIONE,
        )
      ) {
        return { allowed: false };
      }

      // Se l'utente ha "full control" (titolare senza delegati) e il modulo non richiede la firma
      // Allora è _sempre_ modificabile anche se non è in stato di compilazione
      if (isModuleWithoutSignAndFullControl(record, user)) {
        return { allowed: true };
      }

      if (state !== SubmissionModuleState.Compilation) {
        return { allowed: false };
      }

      if (
        record.submission?.datiDelega?.permetteCompilazione === false &&
        record.submission?.idDelegato === user?.utenteLoggato?.idUtente
      ) {
        return { allowed: false };
      }

      return { allowed: true };
    },
  })
  .withAction('.autoComplete', {
    allowed: (record, user, state) => {
      return {
        allowed: isModuleWithoutSignAndFullControl(record, user),
      };
    },
  });
